import React, { useEffect, useMemo, useRef, useState } from 'react';
import { api } from '../../lib/api';
import { buildDateTokens } from '../../lib/dateTokens';
import PreviewSchoolPicker from './PreviewSchoolPicker';

// Sprint F item 6.5 + 6.6 — live preview pane for the TemplateEditor.
// Substitutes BOTH token systems used across the app:
//   - flat:   {{principal_name}}, {{school_name}}  (send-one-off, template-renderer.js)
//   - dotted: {{account.name}}, {{contact.name}}   (cron outreach runner, render-pdf.js)
// Falls back to a synthetic sample account if no contracted/maintained
// accounts exist in this DB so the dropdown is never empty.
//
// A real school's media balance, latest audit and stand-in contact are not in
// the list rows; they come from /api/admin/outreach/token-context, looked up
// the way real sends look them up, so the preview shows what that school
// would receive.
//
// "Preview as" is a searchable picker (PreviewSchoolPicker): the list above
// is its suggestions, and typing searches every school in the CRM. A school
// found by search goes through the same mapping and the same token-context
// lookup as one from the list.

const FOOTER_TOOLTIP = 'Auto-appended for compliance, not editable';

function resolveDotted(path, ctx) {
  return path.split('.').reduce((acc, k) => (acc == null ? acc : acc[k]), ctx);
}

function tokenSubstituteFront(template, flat, ctx) {
  return String(template ?? '').replace(/\{\{\s*([a-zA-Z_][a-zA-Z0-9_.]*)\s*\}\}/g, (_, name) => {
    if (name.includes('.')) {
      const v = resolveDotted(name, ctx);
      return v == null ? '' : String(v);
    }
    return flat[name] ?? '';
  });
}

// TipTap emits an empty `<p></p>` for blank lines, which the prose CSS
// collapses to zero height (margin-collapse on an empty element). Inject a
// <br/> so blank lines actually take up a line of vertical space — matches
// how Gmail / Outlook render empty paragraphs.
function normalizeEmptyParagraphs(html) {
  return String(html ?? '').replace(/<p>(\s|&nbsp;)*<\/p>/gi, '<p><br/></p>');
}

const SAMPLE_ID = '__sample__';
// Shown in place of a token while the school's own value is being looked up.
const LOADING = '…';

// Stand-ins for a real school whose facts could not be loaded (the preview
// never goes blank over a failed lookup — except the media tokens, which read
// empty rather than show another school's number).
const PLACEHOLDER = {
  contactName: 'Sandra Sanchez',
  contactEmail: 'principal@example.org',
  auditScore: 78,
  auditDate: 'April 22',
};

const FALLBACK_SAMPLE = {
  id: SAMPLE_ID,
  name: 'M.S. 308 Bronx Dance Academy',
  shortName: 'M.S. 308',
  dbn: '10X308',
  borough: 'Bronx',
  district: '10',
  slug: 'ms308',
  contactName: 'Sandra Sanchez',
  contactEmail: 'principal@ms308.example',
  auditScore: 78,
  auditDate: 'April 22',
  mediaSessionsRemaining: 3,
  mediaPackageExpires: 'June 30, 2027',
};

// A list or search row (/api/admin/accounts, eligible-accounts) → the
// preview's account shape. Missing values stay null here and are filled from
// the school's token context (withTokenContext).
function toPreviewAccount(a) {
  const pc = a.primary_contact || null;
  return {
    id: a.id,
    name: a.name,
    shortName: a.shortName || a.name,
    dbn: a.dbn || a.typeSchoolDbn || '',
    borough: a.borough || a.typeSchoolBorough || '',
    district: a.district || a.typeSchoolDistrict || '',
    slug: a.slug || '',
    contactName: pc?.name || a.primaryContactName || null,
    contactEmail: pc?.email || a.primaryContactEmail || null,
    auditScore: a.lastAuditScore ?? null,
    auditDate: a.lastAuditDate || null,
  };
}

function deriveSenderName(sender) {
  if (sender?.name) return sender.name;
  const email = sender?.email || '';
  if (email && email.includes('@')) {
    const local = email.split('@')[0].replace(/[._-]+/g, ' ').trim();
    if (local) return local.replace(/\b\w/g, (c) => c.toUpperCase());
  }
  return 'NexSav Outreach';
}

// Fill in what a live list row lacks from the school's token context:
//   loading → "…" for each missing value (the lookup is on its way)
//   ready   → the school's real values (empty where it genuinely has none)
//   error   → the old stand-ins for contact/audit; media tokens stay empty
function withTokenContext(account, entry) {
  if (account.id === SAMPLE_ID) return account;
  const status = entry?.status || 'loading';
  const d = entry?.data || {};
  const pick = (own, fromCtx, placeholder) => {
    if (own != null && own !== '') return own;
    if (status === 'ready') return fromCtx ?? '';
    return status === 'error' ? placeholder : LOADING;
  };
  return {
    ...account,
    contactName: pick(account.contactName, d.contact?.name, PLACEHOLDER.contactName),
    contactEmail: pick(account.contactEmail, d.contact?.email, PLACEHOLDER.contactEmail),
    auditScore: pick(account.auditScore, d.audit?.score, PLACEHOLDER.auditScore),
    auditDate: pick(account.auditDate, d.auditDate, PLACEHOLDER.auditDate),
    mediaSessionsRemaining: status === 'ready' ? (d.mediaSessionsRemaining ?? '') : (status === 'error' ? '' : LOADING),
    mediaPackageExpires: status === 'ready' ? (d.mediaPackageExpires ?? '') : (status === 'error' ? '' : LOADING),
  };
}

function flatTokensFor(account, sender, customTokens) {
  const senderName = deriveSenderName(sender);
  const senderEmail = sender?.email || 'outreach@nexsav.net';
  const senderFirst = senderName.split(' ')[0];
  const customTokenMap = {};
  for (const ct of customTokens) if (ct.key) customTokenMap[ct.key] = ct.value;
  const contactName = String(account.contactName || '');
  const lastName = contactName.split(' ').slice(-1)[0];
  return {
    principal_name: contactName,
    principal_first_name: contactName.split(' ')[0],
    principal_last_name: contactName.split(' ').slice(1).join(' '),
    salutation: lastName ? `Principal ${lastName}` : 'Principal',
    school_name: account.name,
    school_short_name: account.shortName || account.name,
    dbn: account.dbn || '',
    borough: account.borough || '',
    district: account.district || '',
    audit_score: String(account.auditScore ?? ''),
    audit_date: account.auditDate || '',
    unsubscribe_link: 'https://nexsav.net/unsub/SAMPLE',
    nexsav_address: '4747 197th Street, Flushing, NY 11358',
    sender_name: senderName,
    sender_email: senderEmail,
    sender_first_name: senderFirst,
    ...buildDateTokens(new Date()),
    // A real school's balance comes from its token context (withTokenContext);
    // "0" when it has no active package, "…" while loading.
    media_sessions_remaining: account.mediaSessionsRemaining == null ? '' : String(account.mediaSessionsRemaining),
    media_package_expires: account.mediaPackageExpires || '',
    ...customTokenMap,
  };
}

function dottedCtxFor(account, sender, customTokens) {
  const customTokenMap = {};
  for (const ct of customTokens) if (ct.key) customTokenMap[ct.key] = ct.value;
  return {
    account: {
      name: account.name,
      slug: account.slug || '',
      borough: account.borough || '',
      district: account.district || '',
      dbn: account.dbn || '',
    },
    contact: {
      name: account.contactName,
      email: account.contactEmail,
    },
    campaign: {
      name: 'Sample Campaign',
    },
    sender: {
      name: deriveSenderName(sender),
      email: sender?.email || 'outreach@nexsav.net',
    },
    audit: {
      score: account.auditScore ?? '',
      date: account.auditDate || '',
    },
    unsubscribeUrl: 'https://nexsav.net/unsub/SAMPLE',
    custom: customTokenMap,
  };
}

export default function TemplatePreviewPane({ subjectTemplate, bodyHtml, sender, customTokens = [], campaignId, onAccountChange }) {
  const [accounts, setAccounts] = useState([FALLBACK_SAMPLE]);
  const [accountId, setAccountId] = useState(FALLBACK_SAMPLE.id);
  // A school picked from search that is not among the suggestions.
  const [searched, setSearched] = useState(null);
  // Once someone picks a school, a list that loads late doesn't override it.
  const userPickedRef = useRef(false);
  // Per-school token context, by account id: { status, data }.
  const [tokenContexts, setTokenContexts] = useState({});
  const tokenContextsRef = useRef(tokenContexts);
  tokenContextsRef.current = tokenContexts;
  const onAccountChangeRef = useRef(onAccountChange);
  onAccountChangeRef.current = onAccountChange;
  // Sender resolved from the campaign's configured fromUserId / fromEmail.
  // An explicit `sender` prop still wins (used by the send-one-off panel).
  const [campaignSender, setCampaignSender] = useState(null);

  useEffect(() => {
    let alive = true;
    // Sprint K — when this preview pane is mounted inside a campaign step
    // editor, the "Preview as" dropdown should show the SAME accounts the
    // campaign is targeting (the eligible set computed from the targeting
    // step). Without a campaign id we fall back to the generic
    // contracted + maintained schools (the maintained ones are the ones that
    // hold media packages) for the Templates tab and send-one-off contexts.
    const url = campaignId
      ? `/api/admin/outreach/campaigns/${encodeURIComponent(campaignId)}/eligible-accounts?include_accounts=1&limit=100`
      : '/api/admin/accounts?lifecycle_stages=contracted,maintained&limit=50';
    Promise.resolve(api.get(url)).then((d) => {
      if (!alive) return;
      if (d && d.sender) setCampaignSender(d.sender);
      const rawAccounts = (d && d.accounts) || [];
      const live = rawAccounts.map(toPreviewAccount);
      const merged = [FALLBACK_SAMPLE, ...live];
      setAccounts(merged);
      // Prefer a real eligible account by default when one exists, so the
      // preview reflects an actual recipient instead of the synthetic stub.
      if (live.length > 0 && !userPickedRef.current) setAccountId(live[0].id);
    }).catch(() => {});
    return () => { alive = false; };
  }, [campaignId]);

  const listed = accounts.find((a) => a.id === accountId)
    || (searched && searched.id === accountId ? searched : null)
    || FALLBACK_SAMPLE;
  const pickAccount = (a) => {
    userPickedRef.current = true;
    if (!accounts.some((x) => x.id === a.id)) setSearched(a);
    setAccountId(a.id);
  };

  // Tell the editor which school is selected (null for the sample) so its
  // "Send test" renders that school's version.
  useEffect(() => {
    onAccountChangeRef.current?.(listed.id === SAMPLE_ID ? null : listed);
  }, [listed]);

  // Look up the selected school's token context once per school. A response
  // that lands after the selection moved on is dropped (and refetched if the
  // school is selected again), so a slow answer never paints another school.
  useEffect(() => {
    const id = listed.id;
    if (id === SAMPLE_ID || tokenContextsRef.current[id]?.status === 'ready') return undefined;
    let alive = true;
    setTokenContexts((m) => ({ ...m, [id]: { status: 'loading' } }));
    Promise.resolve(api.get(`/api/admin/outreach/token-context?account_id=${encodeURIComponent(id)}`))
      .then((data) => { if (alive) setTokenContexts((m) => ({ ...m, [id]: { status: 'ready', data: data || {} } })); })
      .catch(() => { if (alive) setTokenContexts((m) => ({ ...m, [id]: { status: 'error' } })); });
    return () => { alive = false; };
  }, [listed.id]);

  const tokenContext = tokenContexts[listed.id];
  const selected = useMemo(() => withTokenContext(listed, tokenContext), [listed, tokenContext]);
  const effectiveSender = sender || campaignSender || null;
  const flat = useMemo(() => flatTokensFor(selected, effectiveSender, customTokens), [selected, effectiveSender, customTokens]);
  const ctx = useMemo(() => dottedCtxFor(selected, effectiveSender, customTokens), [selected, effectiveSender, customTokens]);

  const renderedSubject = tokenSubstituteFront(subjectTemplate, flat, ctx);
  const renderedBody = normalizeEmptyParagraphs(tokenSubstituteFront(bodyHtml, flat, ctx));

  return (
    // No overflow-hidden on the frame: the school picker's list drops down over
    // the email. The two halves round (and clip) their own corners instead.
    <div data-testid="template-preview-pane" className="rounded-2xl border border-white/[0.06] bg-white/[0.02]">
      <div className="px-4 py-2 bg-white/[0.02] border-b border-white/[0.04] flex items-center gap-3 flex-wrap rounded-t-[15px]">
        <PreviewSchoolPicker
          suggestions={accounts}
          selected={listed}
          sampleId={SAMPLE_ID}
          onSelect={pickAccount}
          toAccount={toPreviewAccount}
        />
      </div>
      <div className="px-5 py-4 bg-white text-black rounded-b-[15px] overflow-hidden">
        <div className="text-[11px] text-gray-500 mb-1">From: {flat.sender_name} &lt;{flat.sender_email}&gt;</div>
        <div className="text-[11px] text-gray-500 mb-1">
          To: {flat.principal_name || selected.contactEmail ? <>{flat.principal_name} &lt;{selected.contactEmail}&gt;</> : 'no contact on file'}
        </div>
        <div className="text-[14px] font-semibold mt-2 mb-3" data-testid="preview-subject">{renderedSubject}</div>
        {/*
          Tailwind preflight strips the default browser styles for ul/ol
          (list-style: none; padding: 0), and the project doesn't include
          @tailwindcss/typography — so the `prose` class here is a no-op.
          Without this scoped reset, bullet/numbered lists from TipTap
          render as flat text with no markers. We restore email-client
          defaults so the preview matches what Gmail/Outlook will show.
        */}
        <style>{`
          .preview-body p { margin: 0 0 0.75em 0; }
          .preview-body ul { list-style: disc; padding-left: 1.5em; margin: 0.5em 0; }
          .preview-body ol { list-style: decimal; padding-left: 1.5em; margin: 0.5em 0; }
          .preview-body li { margin: 0.25em 0; }
          .preview-body li > p { margin: 0; }
          .preview-body strong { font-weight: 600; }
          .preview-body em { font-style: italic; }
          .preview-body u { text-decoration: underline; }
          .preview-body s { text-decoration: line-through; }
          .preview-body a { color: #2563eb; text-decoration: underline; }
          .preview-body h1 { font-size: 1.5em; font-weight: 600; margin: 0.75em 0 0.5em; }
          .preview-body h2 { font-size: 1.25em; font-weight: 600; margin: 0.75em 0 0.5em; }
          .preview-body h3 { font-size: 1.1em; font-weight: 600; margin: 0.75em 0 0.5em; }
          .preview-body hr { border: 0; border-top: 1px solid #e5e7eb; margin: 1em 0; }
          .preview-body img { max-width: 100%; height: auto; border-radius: 4px; margin: 0.5em 0; }
        `}</style>
        <div className="preview-body max-w-none text-[14px] leading-relaxed" dangerouslySetInnerHTML={{ __html: renderedBody }} data-testid="preview-body" />
        <div
          data-testid="preview-canspam-footer"
          title={FOOTER_TOOLTIP}
          className="mt-8 pt-3 border-t border-gray-200 text-[10px] text-gray-400 text-center cursor-not-allowed select-none"
        >
          {flat.nexsav_address}<br/>
          NexSav LLC — Vendor # NEX833087<br/>
          <span className="underline">Unsubscribe</span> — sent on behalf of {flat.sender_name}
        </div>
      </div>
    </div>
  );
}
